<template>
  <section class="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20" id="contact" aria-labelledby="contact-heading-t1">
    <div class="grid grid-cols-1 gap-16" :class="{ 'md:grid-cols-2': hasSchedule }">
      <div>
        <h2 id="contact-heading-t1" class="text-3xl font-bold tracking-tight mb-8 text-t1-neutral-dark">{{ $t('public.contactUs') }}</h2>
        <div class="space-y-6">
          <div v-for="c in contacts" :key="c.uuid" class="flex gap-4">
            <span class="material-symbols-outlined text-t1-neutral-muted shrink-0" :aria-hidden="true">
              {{ contactIcon(c.type) }}
            </span>
            <div class="min-w-0">
              <p class="font-bold text-t1-neutral-dark">{{ contactTypeLabel(c.type) }}{{ c.label ? ` — ${c.label}` : '' }}</p>
              <template v-if="isLinkType(c.type) && contactVal(c)">
                <a
                  :href="contactVal(c)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center min-h-[44px] py-2 -my-2 rms-link-primary hover:underline font-medium break-all rounded"
                >
                  {{ contactVal(c) }}
                </a>
              </template>
              <template v-else-if="c.type === 'email' && contactVal(c)">
                <a
                  :href="`mailto:${contactVal(c)}`"
                  class="inline-flex items-center min-h-[44px] py-2 -my-2 rms-link-primary hover:underline font-medium break-all rounded"
                >
                  {{ contactVal(c) }}
                </a>
              </template>
              <template v-else-if="c.type === 'whatsapp' && whatsAppUrl(contactVal(c))">
                <a
                  :href="whatsAppUrl(contactVal(c))"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center min-h-[44px] py-2 -my-2 rms-link-primary hover:underline font-medium rounded"
                >
                  {{ contactVal(c) }}
                </a>
                <span class="text-t1-neutral-muted text-sm ml-1">(WhatsApp)</span>
              </template>
              <template v-else-if="isCallable(c.type)">
                <a
                  :href="`tel:${contactVal(c).replace(/\s/g, '')}`"
                  class="inline-flex items-center min-h-[44px] py-2 -my-2 rms-link-primary hover:underline font-medium rounded"
                >
                  {{ contactVal(c) }}
                </a>
              </template>
              <p v-else class="text-t1-neutral-muted">{{ contactVal(c) }}</p>
            </div>
          </div>
          <div v-if="!contacts.length" class="text-t1-neutral-muted">{{ $t('public.noContactListed') }}</div>
        </div>
      </div>
      <div v-if="hasSchedule" id="hours">
        <h2 class="text-3xl font-bold tracking-tight mb-8 text-t1-neutral-dark">{{ $t('public.openingHours') }}</h2>
        <PublicOpenStatusBadge :operating-hours="operatingHours" variant="contact" />
        <div class="space-y-3">
          <template v-if="hoursSummary.is24_7">
            <div
              class="inline-flex min-h-[44px] items-center gap-3 rounded-lg border border-t1-border bg-[color:color-mix(in_srgb,var(--t1-primary)_10%,transparent)] px-4 py-3"
              role="status"
              :aria-label="$t('public.open24_7')"
            >
              <span class="material-symbols-outlined text-2xl text-t1-neutral-muted shrink-0" aria-hidden="true">schedule</span>
              <span class="font-semibold text-t1-neutral-dark">{{ $t('public.open24_7') }}</span>
            </div>
          </template>
          <template v-else>
            <div
              v-for="row in hoursSummary.perDay"
              :key="row.day"
              class="flex justify-between border-b border-t1-border pb-2"
            >
              <span class="font-medium text-t1-neutral-dark">{{ $t('public.day' + row.day.charAt(0).toUpperCase() + row.day.slice(1)) }}</span>
              <span class="text-t1-neutral-muted">{{ row.text === 'Closed' ? $t('public.closedNow') : row.text }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import PublicOpenStatusBadge from '@/components/public/PublicOpenStatusBadge.vue'
import { getOperatingHoursSummary, hasOperatingHoursSchedule } from '@/utils/availability'
import { buildWhatsAppUrl, contactValue, isLinkType } from '@/utils/contact'

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  operatingHours: { type: Object, default: null },
})

const { t } = useI18n()
const hoursSummary = computed(() => getOperatingHoursSummary(props.operatingHours))
const hasSchedule = computed(() => hasOperatingHoursSchedule(props.operatingHours))

function contactVal(c) {
  return contactValue(c)
}

function contactIcon(type) {
  if (type === 'email') return 'mail'
  if (type === 'whatsapp') return 'chat'
  if (isLinkType(type)) return 'link'
  return 'call'
}

function contactTypeLabel(type) {
  const key = 'public.' + type
  try {
    const translated = t(key)
    if (translated && translated !== key) return translated
  } catch (_) {}
  return type
}

function whatsAppUrl(number) {
  return buildWhatsAppUrl(number)
}

function isCallable(type) {
  return ['mobile', 'phone', 'fax'].includes(type)
}
</script>
